import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import { buildStoredSearchPayload } from '../utils/searchStorage';
import { formatPrice } from '../utils/currencyPreference';
import {
  DEFAULT_CITY_CARD_IMAGE,
  enrichPopularCity,
  getCityCardImage,
} from '../utils/cityCardImages';
import './PopularCities.css';

export const DEFAULT_POPULAR_CITIES = [
  enrichPopularCity({
    id: 'bengaluru',
    name: 'Bengaluru',
    propertyCount: 120,
    minPrice: 2500,
  }),
  enrichPopularCity({
    id: 'mysuru',
    name: 'Mysuru',
    propertyCount: 85,
    minPrice: 1800,
  }),
  enrichPopularCity({
    id: 'mangaluru',
    name: 'Mangaluru',
    propertyCount: 60,
    minPrice: 1500,
  }),
  enrichPopularCity({
    id: 'goa',
    name: 'Goa',
    propertyCount: 200,
    minPrice: 2200,
  }),
  enrichPopularCity({
    id: 'dubai',
    name: 'Dubai',
    propertyCount: 150,
    minPrice: 8500,
  }),
];

const FETCH_LIMIT = 10;
const SECONDS_PER_CARD = 4;

const getLastSearchKey = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
      return `lastSearch_${userObj._id || userObj.id}`;
    }
  } catch {
    /* ignore */
  }
  return 'lastSearch';
};

const mergeWithDefaults = (apiCities = []) => {
  const merged = apiCities.map(enrichPopularCity);
  const seen = new Set(merged.map((c) => String(c.name || '').toLowerCase().trim()));

  DEFAULT_POPULAR_CITIES.forEach((def) => {
    const key = String(def.name).toLowerCase().trim();
    if (!seen.has(key)) {
      merged.push(def);
      seen.add(key);
    }
  });

  return merged;
};

const formatPropertyCount = (count) => {
  const n = Number(count) || 0;
  if (n <= 0) return 'Stays available';
  return `${n}+ Properties`;
};

const PopularCities = () => {
  const navigate = useNavigate();
  const [cities, setCities] = useState(DEFAULT_POPULAR_CITIES);
  const [loading, setLoading] = useState(true);
  const [searchingId, setSearchingId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const carouselCities = useMemo(() => {
    if (cities.length === 0) return [];
    return [...cities, ...cities];
  }, [cities]);

  const marqueeDuration = useMemo(
    () => `${Math.max(cities.length * SECONDS_PER_CARD, 20)}s`,
    [cities.length]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchPopularCities = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          getApiUrl(`/api/properties/popular-cities?limit=${FETCH_LIMIT}`)
        );
        const contentType = response.headers.get('content-type');
        if (!contentType?.includes('application/json')) {
          if (!cancelled) setCities(DEFAULT_POPULAR_CITIES);
          return;
        }

        const result = await response.json();
        if (!cancelled && result.success && Array.isArray(result.cities) && result.cities.length > 0) {
          setCities(mergeWithDefaults(result.cities));
        } else if (!cancelled) {
          setCities(DEFAULT_POPULAR_CITIES);
        }
      } catch (error) {
        console.error('Error fetching popular cities:', error);
        if (!cancelled) setCities(DEFAULT_POPULAR_CITIES);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPopularCities();
    return () => {
      cancelled = true;
    };
  }, []);

  const runCitySearch = useCallback(
    async (city) => {
      const cityName = String(city?.name || '').trim();
      if (!cityName) return;

      setSearchingId(city.id || cityName);

      const guests = { adults: 2, children: 0, infants: 0, rooms: 1, pets: 0 };
      const priceRange = [0, 50000];

      try {
        localStorage.setItem(
          getLastSearchKey(),
          JSON.stringify({
            location: cityName,
            checkIn: null,
            checkOut: null,
            guests,
            propertyType: 'all',
            priceRange,
            updatedAt: new Date().toISOString(),
          })
        );

        const queryParams = new URLSearchParams({
          q: cityName,
          limit: '20',
          minPrice: String(priceRange[0]),
          maxPrice: String(priceRange[1]),
        });

        const response = await fetch(`${getApiUrl('/api/search')}?${queryParams.toString()}`);
        const contentType = response.headers.get('content-type');
        if (!contentType?.includes('application/json')) {
          throw new Error('Invalid search response');
        }

        const json = await response.json();
        if (!response.ok || !json?.success) {
          throw new Error(json?.message || 'Search failed');
        }

        localStorage.setItem(
          'searchResults',
          JSON.stringify(
            buildStoredSearchPayload(json, cityName, {
              checkIn: null,
              checkOut: null,
              guests,
              propertyType: 'all',
              priceRange,
            })
          )
        );

        navigate('/search');
      } catch (error) {
        console.error('Popular city search error:', error);
        navigate(`/search?q=${encodeURIComponent(cityName)}`);
      } finally {
        setSearchingId(null);
      }
    },
    [navigate]
  );

  const handleImageError = useCallback((e, cityName) => {
    const fallback = getCityCardImage(cityName) || DEFAULT_CITY_CARD_IMAGE;
    if (e.currentTarget.src !== fallback) {
      e.currentTarget.src = fallback;
    }
  }, []);

  const renderCityCard = (city, index) => {
    const isBusy = searchingId === (city.id || city.name);
    const cardKey = `${city.id || city.name}-${index}`;
    const imageSrc = city.image || getCityCardImage(city.name);

    return (
      <button
        key={cardKey}
        type="button"
        className={`popular-city-card${isBusy ? ' popular-city-card--busy' : ''}`}
        onClick={() => runCitySearch(city)}
        disabled={Boolean(searchingId)}
        aria-label={`Search stays in ${city.name}`}
      >
        <img
          src={imageSrc}
          alt={city.name}
          loading="lazy"
          decoding="async"
          onError={(e) => handleImageError(e, city.name)}
        />
        <div className="popular-city-card-overlay" />
        <div className="popular-city-card-body">
          <h3>{city.name}</h3>
          <p>{formatPropertyCount(city.propertyCount)}</p>
          <span>
            From{' '}
            {city.minPrice != null && city.minPrice > 0
              ? formatPrice(city.minPrice)
              : '₹1,500'}
          </span>
        </div>
        {isBusy && <span className="popular-city-card-spinner" aria-hidden="true" />}
      </button>
    );
  };

  return (
    <section className="popular-cities-section home-section" aria-labelledby="popular-cities-heading">
      <div className="home-section-header split">
        <h2 id="popular-cities-heading" className="home-section-title">
          Popular Cities
        </h2>
        <button
          type="button"
          className="popular-cities-view-all"
          onClick={() => navigate('/search')}
        >
          View all
        </button>
      </div>

      <div
        className="popular-cities-carousel"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        {loading ? (
          <div className="popular-cities-marquee-viewport">
            <div className="popular-cities-marquee-track popular-cities-marquee-track--static">
              {DEFAULT_POPULAR_CITIES.map((city) => (
                <div key={city.id} className="popular-city-card popular-city-card--skeleton" />
              ))}
            </div>
          </div>
        ) : (
          <div className="popular-cities-marquee-viewport">
            <div
              className={`popular-cities-marquee-track${isPaused ? ' is-paused' : ''}`}
              style={{ '--marquee-duration': marqueeDuration }}
              aria-label="Popular cities carousel"
            >
              {carouselCities.map((city, index) => renderCityCard(city, index))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PopularCities;
